import { Bell, Home, type LucideIcon, MessageSquare, MessageSquarePlus, Settings, Ticket, User } from 'lucide-react';

import {
  PATH_HUB_ACCOUNT_DETAILS,
  PATH_HUB_HOME,
  PATH_HUB_PLAN_DETAILS,
  PATH_HUB_SESSIONS_PAGE,
  PATH_HUB_STUDENTS,
  PATH_HUB_SUBSCRIPTIONS,
  PATH_HUB_VOUCHERS,
} from '../../legacy/pagePath';

export type AppSidebarItem = {
  /** Stable identity for React keys and active-state lookups. */
  key: string;
  label: string;
  icon: LucideIcon;
  /** Internal route — rendered as a router Link. Mutually exclusive with onClick. */
  to?: string;
  /** Match the route exactly (used for Home). Defaults to prefix match. */
  exact?: boolean;
  /** Action item — rendered as a button. Mutually exclusive with to. */
  onClick?: () => void;
};

export type NavContext = {
  userType: 'Lead' | 'Account' | null;
  /** Book-a-session is only shown when the account has a bookable Course enrolment. */
  showBookSession: boolean;
  /** Student Hub is only shown when the account has a valid subscription. */
  showStudentHub: boolean;
  onBookSession: () => void;
  onStudentHub: () => void;
};

export type AppSidebarNav = {
  /** Primary nav entries rendered in the sidebar body. */
  items: AppSidebarItem[];
  /** Pinned to the bottom of the sidebar, above the account switcher. */
  bookSessionItem: AppSidebarItem | null;
};

// Builds the ordered sidebar nav for the current account, mirroring the nav
// model that previously lived in HubHeader (NAV_ITEMS + getNavOptions).
//
// Non-"Account" users (Leads) only see Home and their account details.
export const getNavItems = (ctx: NavContext): AppSidebarNav => {
  const home: AppSidebarItem = { key: 'home', label: 'Home', icon: Home, to: PATH_HUB_HOME, exact: true };

  if (ctx.userType !== 'Account') {
    return {
      items: [home, { key: 'account', label: 'Account', icon: User, to: PATH_HUB_ACCOUNT_DETAILS }],
      bookSessionItem: null,
    };
  }

  const items: AppSidebarItem[] = [
    home,
    { key: 'sessions', label: 'Sessions', icon: MessageSquare, to: PATH_HUB_SESSIONS_PAGE },
    { key: 'students', label: 'Students', icon: User, to: PATH_HUB_STUDENTS },
    { key: 'vouchers', label: 'Cluey Vouchers', icon: Ticket, to: PATH_HUB_VOUCHERS },
    { key: 'subscriptions', label: 'Subscriptions', icon: Bell, to: PATH_HUB_SUBSCRIPTIONS },
    { key: 'account', label: 'Account', icon: Settings, to: PATH_HUB_PLAN_DETAILS },
  ];

  const bookSessionItem: AppSidebarItem | null = ctx.showBookSession
    ? { key: 'book-session', label: 'Book a session', icon: MessageSquarePlus, onClick: ctx.onBookSession }
    : null;

  return { items, bookSessionItem };
};
